import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Download } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  component: AnalyticsPage,
});

type RangeKey = "7d" | "30d" | "90d" | "12m";
const RANGES: Record<RangeKey, { label: string; days: number; bucket: "day" | "month" }> = {
  "7d":  { label: "Last 7 days",  days: 7,   bucket: "day" },
  "30d": { label: "Last 30 days", days: 30,  bucket: "day" },
  "90d": { label: "Last 90 days", days: 90,  bucket: "day" },
  "12m": { label: "Last 12 months", days: 365, bucket: "month" },
};

function startOf(d: Date, bucket: "day" | "month") {
  const x = new Date(d);
  if (bucket === "day") x.setHours(0, 0, 0, 0);
  else { x.setDate(1); x.setHours(0, 0, 0, 0); }
  return x;
}
function bucketKey(d: Date, bucket: "day" | "month") {
  const x = startOf(d, bucket);
  return bucket === "day"
    ? x.toISOString().slice(0, 10)
    : `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
}
function buildBuckets(days: number, bucket: "day" | "month") {
  const end = startOf(new Date(), bucket);
  const out: string[] = [];
  if (bucket === "day") {
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(end); d.setDate(end.getDate() - i);
      out.push(bucketKey(d, bucket));
    }
  } else {
    const months = Math.ceil(days / 30);
    for (let i = months - 1; i >= 0; i--) {
      const d = new Date(end); d.setMonth(end.getMonth() - i);
      out.push(bucketKey(d, bucket));
    }
  }
  return out;
}

function toCsv(rows: Record<string, unknown>[]): string {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map(r => headers.map(h => esc(r[h])).join(","))].join("\n");
}
function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) { toast.info("Nothing to export"); return; }
  const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function AnalyticsPage() {
  const [range, setRange] = useState<RangeKey>("30d");
  const cfg = RANGES[range];
  const sinceIso = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - cfg.days); return d.toISOString();
  }, [cfg.days]);

  const events = useQuery({
    queryKey: ["analytics_events", range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("analytics_events")
        .select("event, properties, path, user_id, created_at")
        .gte("created_at", sinceIso)
        .order("created_at", { ascending: false })
        .limit(10000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const orders = useQuery({
    queryKey: ["analytics_orders", range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, status, total_cents, currency, created_at, paid_at, user_id, affiliate_code")
        .gte("created_at", sinceIso)
        .limit(10000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const items = useQuery({
    queryKey: ["analytics_items", range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_items")
        .select("product_id, product_name, qty, unit_price_cents, created_at")
        .gte("created_at", sinceIso)
        .limit(10000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const buckets = useMemo(() => buildBuckets(cfg.days, cfg.bucket), [cfg.days, cfg.bucket]);

  // Traffic series
  const traffic = useMemo(() => {
    const seed = Object.fromEntries(buckets.map(b => [b, { date: b, pageviews: 0, sessions: 0, signups: 0 }]));
    const sessionsByBucket: Record<string, Set<string>> = {};
    for (const e of events.data ?? []) {
      const k = bucketKey(new Date(e.created_at as string), cfg.bucket);
      if (!seed[k]) continue;
      if (e.event === "pageview") seed[k].pageviews += 1;
      if (e.event === "signup") seed[k].signups += 1;
      const sid = (e.properties as any)?.session_id ?? e.user_id ?? null;
      if (sid) {
        sessionsByBucket[k] ??= new Set();
        sessionsByBucket[k].add(String(sid));
      }
    }
    for (const k of Object.keys(seed)) seed[k].sessions = sessionsByBucket[k]?.size ?? 0;
    return buckets.map(b => seed[b]);
  }, [events.data, buckets, cfg.bucket]);

  // Revenue series
  const revenue = useMemo(() => {
    const seed = Object.fromEntries(buckets.map(b => [b, { date: b, revenue: 0, refunds: 0, orders: 0 }]));
    for (const o of orders.data ?? []) {
      const k = bucketKey(new Date(o.created_at as string), cfg.bucket);
      if (!seed[k]) continue;
      const amt = (o.total_cents ?? 0) / 100;
      if (o.status === "refunded") seed[k].refunds += amt;
      else if (o.status === "paid" || o.status === "fulfilled") {
        seed[k].revenue += amt;
        seed[k].orders += 1;
      }
    }
    return buckets.map(b => seed[b]);
  }, [orders.data, buckets, cfg.bucket]);

  // KPIs
  const kpis = useMemo(() => {
    const totalPV = traffic.reduce((s, r) => s + r.pageviews, 0);
    const totalSessions = traffic.reduce((s, r) => s + r.sessions, 0);
    const totalSignups = traffic.reduce((s, r) => s + r.signups, 0);
    const totalRev = revenue.reduce((s, r) => s + r.revenue, 0);
    const totalRefunds = revenue.reduce((s, r) => s + r.refunds, 0);
    const totalOrders = revenue.reduce((s, r) => s + r.orders, 0);
    const paidUsers = new Set(
      (orders.data ?? [])
        .filter(o => (o.status === "paid" || o.status === "fulfilled") && o.user_id)
        .map(o => o.user_id as string)
    );
    const conv = totalSessions > 0 ? (paidUsers.size / totalSessions) * 100 : 0;
    const aov = totalOrders > 0 ? totalRev / totalOrders : 0;
    return { totalPV, totalSessions, totalSignups, totalRev, totalRefunds, totalOrders, conv, aov, paidCount: paidUsers.size };
  }, [traffic, revenue, orders.data]);

  // Funnel
  const funnel = useMemo(() => {
    const visitors = new Set<string>();
    const signups = new Set<string>();
    for (const e of events.data ?? []) {
      const sid = (e.properties as any)?.session_id ?? e.user_id;
      if (sid && e.event === "pageview") visitors.add(String(sid));
      if (e.event === "signup" && e.user_id) signups.add(String(e.user_id));
    }
    const paid = new Set(
      (orders.data ?? [])
        .filter(o => (o.status === "paid" || o.status === "fulfilled") && o.user_id)
        .map(o => String(o.user_id))
    );
    return [
      { stage: "Visitors", count: visitors.size || kpis.totalSessions },
      { stage: "Signups",  count: signups.size  || kpis.totalSignups },
      { stage: "Paid",     count: paid.size },
    ];
  }, [events.data, orders.data, kpis.totalSessions, kpis.totalSignups]);

  // Top pages
  const topPages = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of events.data ?? []) {
      if (e.event !== "pageview" || !e.path) continue;
      map.set(e.path, (map.get(e.path) ?? 0) + 1);
    }
    return Array.from(map, ([path, views]) => ({ path, views }))
      .sort((a, b) => b.views - a.views).slice(0, 10);
  }, [events.data]);

  // Top referrers
  const topReferrers = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of events.data ?? []) {
      const ref = (e.properties as any)?.referrer as string | undefined;
      if (!ref) continue;
      let host = ref;
      try { host = new URL(ref).hostname || ref; } catch { /* noop */ }
      map.set(host, (map.get(host) ?? 0) + 1);
    }
    return Array.from(map, ([source, hits]) => ({ source, hits }))
      .sort((a, b) => b.hits - a.hits).slice(0, 10);
  }, [events.data]);

  // Top products
  const topProducts = useMemo(() => {
    const map = new Map<string, { name: string; qty: number; revenue: number }>();
    for (const it of items.data ?? []) {
      const key = (it.product_id as string) ?? (it.product_name as string);
      const cur = map.get(key) ?? { name: (it.product_name as string) ?? "Unknown", qty: 0, revenue: 0 };
      cur.qty += (it.qty as number) ?? 0;
      cur.revenue += (((it.qty as number) ?? 0) * ((it.unit_price_cents as number) ?? 0)) / 100;
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  }, [items.data]);

  const loading = events.isLoading || orders.isLoading || items.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <AdminPageHeader
          title="Analytics & reports"
          description="Traffic, revenue, and conversion insights across the platform."
        />
        <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(RANGES).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Pageviews" value={kpis.totalPV.toLocaleString()} />
        <Kpi label="Sessions" value={kpis.totalSessions.toLocaleString()} />
        <Kpi label="Revenue" value={`$${kpis.totalRev.toFixed(2)}`} hint={`${kpis.totalOrders} orders`} />
        <Kpi label="Conversion" value={`${kpis.conv.toFixed(2)}%`} hint={`AOV $${kpis.aov.toFixed(2)}`} />
      </div>

      <Tabs defaultValue="traffic">
        <PillTabsList search>
          <PillTabsTrigger value="traffic">Traffic</PillTabsTrigger>
          <PillTabsTrigger value="revenue">Revenue</PillTabsTrigger>
          <PillTabsTrigger value="funnel">Funnel</PillTabsTrigger>
          <PillTabsTrigger value="top">Top lists</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="traffic" className="space-y-4">
          <ChartCard
            title="Sessions & pageviews"
            onExport={() => downloadCsv(`traffic-${range}.csv`, traffic)}
            loading={loading}
          >
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={traffic}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Area type="monotone" dataKey="pageviews" stroke="hsl(var(--primary))" fill="hsl(var(--primary)/0.2)" />
                <Area type="monotone" dataKey="sessions"  stroke="hsl(var(--accent-foreground))" fill="hsl(var(--accent)/0.4)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4">
          <ChartCard
            title="Revenue & refunds"
            onExport={() => downloadCsv(`revenue-${range}.csv`, revenue)}
            loading={loading}
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" />
                <Bar dataKey="refunds" fill="hsl(var(--destructive))" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Orders per period" loading={loading}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Line type="monotone" dataKey="orders" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </TabsContent>

        <TabsContent value="funnel" className="space-y-4">
          <ChartCard
            title="Visitor → Signup → Paid"
            onExport={() => downloadCsv(`funnel-${range}.csv`, funnel)}
            loading={loading}
          >
            <div className="space-y-3">
              {funnel.map((step, i) => {
                const top = funnel[0].count || 1;
                const pct = (step.count / top) * 100;
                const prev = i > 0 ? funnel[i - 1].count : null;
                const conv = prev ? (step.count / (prev || 1)) * 100 : null;
                return (
                  <div key={step.stage}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{step.stage}</span>
                      <span className="text-muted-foreground">
                        {step.count.toLocaleString()}
                        {conv != null && <span className="ml-2">({conv.toFixed(1)}%)</span>}
                      </span>
                    </div>
                    <div className="h-8 bg-secondary rounded overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </ChartCard>
        </TabsContent>

        <TabsContent value="top" className="grid gap-4 lg:grid-cols-2">
          <TopTable
            title="Top products"
            rows={topProducts.map(p => ({ name: p.name, qty: p.qty, revenue: `$${p.revenue.toFixed(2)}` }))}
            columns={["name", "qty", "revenue"]}
            onExport={() => downloadCsv(`top-products-${range}.csv`, topProducts)}
            loading={loading}
          />
          <TopTable
            title="Top pages"
            rows={topPages}
            columns={["path", "views"]}
            onExport={() => downloadCsv(`top-pages-${range}.csv`, topPages)}
            loading={loading}
          />
          <TopTable
            title="Top referrers"
            rows={topReferrers}
            columns={["source", "hits"]}
            onExport={() => downloadCsv(`top-referrers-${range}.csv`, topReferrers)}
            loading={loading}
          />
          <Card>
            <CardHeader><CardTitle className="text-base">Summary</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row k="Refunds" v={`$${kpis.totalRefunds.toFixed(2)}`} />
              <Row k="Paying customers" v={kpis.paidCount.toLocaleString()} />
              <Row k="Signups" v={kpis.totalSignups.toLocaleString()} />
              <Row k="AOV" v={`$${kpis.aov.toFixed(2)}`} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between border-b border-border/60 pb-2 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function ChartCard({
  title, children, onExport, loading,
}: { title: string; children: React.ReactNode; onExport?: () => void; loading?: boolean }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{title}</CardTitle>
        {onExport && (
          <Button size="sm" variant="outline" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" /> CSV
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? <div className="h-[220px] grid place-items-center text-muted-foreground text-sm">Loading…</div> : children}
      </CardContent>
    </Card>
  );
}

function TopTable({
  title, rows, columns, onExport, loading,
}: {
  title: string;
  rows: Record<string, unknown>[];
  columns: string[];
  onExport?: () => void;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{title}</CardTitle>
        {onExport && (
          <Button size="sm" variant="outline" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" /> CSV
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs">
            <tr>{columns.map(c => <th key={c} className="px-4 py-2 text-left capitalize">{c}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={columns.length} className="px-4 py-6 text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-6 text-muted-foreground">No data yet.</td></tr>
            )}
            {!loading && rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                {columns.map(c => <td key={c} className="px-4 py-2">{String(r[c] ?? "—")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
